import { Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useLanguageStore } from '../../store/languageStore'
import { useUiLayoutStore } from '../../store/uiLayoutStore'
import { getAppVersion } from '../../utils/version'
import ErrorBoundary from '../common/ErrorBoundary'
import Sidebar from './Sidebar'
import Header from './Header'
import Footer from './Footer'
import InternalTabs from './InternalTabs'
import KeepAliveOutlet from './KeepAliveOutlet'
import MaxOpenTabsDialog from './MaxOpenTabsDialog'
import RecentItemsPanel from './RecentItemsPanel'
import UpdateNotification from '../common/UpdateNotification'
import VoiceAssistant from '../common/VoiceAssistant'
import AutoUpdateManager from '../updates/AutoUpdateManager'
import CommandPalette from '../common/CommandPalette'
import FeedbackModal from '../common/FeedbackModal'
import FeedbackSoundGate from '../common/FeedbackSoundGate'
import ImmediateActionFeedback from '../common/ImmediateActionFeedback'
import FuelStationLiveToasts from '../fuelStation/FuelStationLiveToasts'
import { useFuelStationRealtime } from '../../hooks/useFuelStationRealtime'
import { useNotificationEngineRealtime } from '../../hooks/useNotificationEngineRealtime'
import ScreenViewHost from '../screenView/ScreenViewHost'
import SmartGuideButton from '../guide/SmartGuideButton'
import PageSettingsButton from '../settings/PageSettingsButton'
import { FiMinimize2 } from 'react-icons/fi'
import PageRefreshButton from './PageRefreshButton'
import { useConnectionStatus } from '../../hooks/useConnectionStatus'
import { useSignalPolling } from '../../hooks/useSignalPolling'
import { usePresenceReporter } from '../../hooks/usePresenceReporter'
import { registerClientInSignalSystem, isClientRegisteredInSignalSystem } from '../../services/signalClientRegistration'
import { getRemoteHeartbeatIntervalMs, withHeartbeatJitter } from '../../services/apiConfig'

function FooterWrapper({ children }: { children: (props: { footerVisible: boolean; setFooterVisible: (visible: boolean) => void }) => React.ReactNode }) {
  const [footerVisible, setFooterVisible] = useState<boolean>(() => {
    const saved = localStorage.getItem('footer-visible')
    return saved !== null ? saved === 'true' : true
  })

  useEffect(() => {
    localStorage.setItem('footer-visible', footerVisible.toString())
  }, [footerVisible])

  return <>{children({ footerVisible, setFooterVisible })}</>
}

export default function Layout() {
  const location = useLocation()
  const { isAuthenticated, logout } = useAuthStore()
  const { language, direction } = useLanguageStore()
  const { isContentFullscreen, toggleContentFullscreen, setContentFullscreen } = useUiLayoutStore()
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [currentVersion, setCurrentVersion] = useState<string>('1.0.0')
  const isRTL = direction === 'rtl'

  useConnectionStatus()

  useEffect(() => {
    if (isAuthenticated && !isClientRegisteredInSignalSystem()) {
      registerClientInSignalSystem(8080).catch(console.error)
    }
  }, [isAuthenticated])

  const signalPollMs = useMemo(
    () => withHeartbeatJitter(getRemoteHeartbeatIntervalMs(60_000)),
    [],
  )
  useSignalPolling(true, signalPollMs)
  usePresenceReporter(isAuthenticated)
  useFuelStationRealtime()
  useNotificationEngineRealtime()

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  useEffect(() => {
    if (!isContentFullscreen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContentFullscreen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isContentFullscreen, setContentFullscreen])

  useEffect(() => {
    setCurrentVersion(getAppVersion())
  }, [])

  // Sticky Action Bars: toggle the `is-stuck` shadow on `.sticky-action-bar`
  useEffect(() => {
    const update = () => {
      document.querySelectorAll<HTMLElement>('.sticky-action-bar').forEach((el) => {
        let scroller: HTMLElement | null = el.parentElement
        while (scroller) {
          const overflowY = getComputedStyle(scroller).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') break
          scroller = scroller.parentElement
        }
        const scrollerTop = scroller ? scroller.getBoundingClientRect().top : 0
        el.classList.toggle('is-stuck', el.getBoundingClientRect().top - scrollerTop <= 1)
      })
    }
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    update()
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Keep the same tree when toggling fullscreen so KeepAlive panes are not remounted.
  return (
    <div
      className={
        isContentFullscreen
          ? 'fixed inset-0 z-[200] flex flex-col transition-all duration-300'
          : 'flex h-screen transition-all duration-300'
      }
      style={{
        backgroundColor: 'var(--bg-primary)',
        flexDirection: isContentFullscreen ? 'column' : 'row',
        direction: direction,
      }}
      dir={direction}
    >
      {!isContentFullscreen && (
        <div style={{ order: 1, height: '100vh' }}>
          <Sidebar
            key={`sidebar-${direction}-${language}`}
            currentPath={location.pathname}
            isOpen={isSidebarOpen}
            onToggle={toggleSidebar}
          />
        </div>
      )}

      <div
        className={
          isContentFullscreen
            ? 'relative flex-1 flex flex-col overflow-hidden h-full'
            : 'flex-1 flex flex-col overflow-hidden'
        }
        style={{ order: 2 }}
      >
        {!isContentFullscreen && (
          <div className="flex-shrink-0">
            <Header onLogout={logout} onToggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
          </div>
        )}

        {!isContentFullscreen && (
          <div className="flex-shrink-0">
            <InternalTabs />
          </div>
        )}

        {isContentFullscreen && (
          <div
            className="absolute z-[210] flex items-center gap-2"
            style={{
              top: 12,
              ...(isRTL ? { left: 12 } : { right: 12 }),
            }}
          >
            <PageRefreshButton
              iconSize={18}
              className="flex items-center justify-center p-2 rounded-xl shadow-lg hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
            />
            <button
              type="button"
              onClick={toggleContentFullscreen}
              className="flex items-center justify-center p-2 rounded-xl shadow-lg hover:opacity-90 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, var(--accent-color) 0%, var(--accent-color-hover) 100%)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
              title={isRTL ? 'إلغاء ملء الشاشة (Esc)' : 'Exit Fullscreen (Esc)'}
            >
              <FiMinimize2 size={18} />
            </button>
          </div>
        )}

        <FooterWrapper>
          {({ footerVisible, setFooterVisible }) => (
            <>
              <main
                className={`flex-1 overflow-y-auto custom-scrollbar ${isContentFullscreen ? 'p-4 sm:p-5' : 'p-4 sm:p-6'}`}
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  paddingBottom: isContentFullscreen ? undefined : (footerVisible ? '200px' : '80px'),
                }}
              >
                <ErrorBoundary>
                  <div className="relative h-full">
                    <KeepAliveOutlet />
                    {!isContentFullscreen && <div className="h-20 w-full" />}
                  </div>
                </ErrorBoundary>
              </main>

              {!isContentFullscreen && (
                <Footer isVisible={footerVisible} onToggleVisibility={setFooterVisible} />
              )}

              {!isContentFullscreen && (
                <div
                  className="fixed z-40 px-3 py-2 rounded-lg shadow-lg"
                  style={{
                    bottom: footerVisible ? '70px' : '30px',
                    right: isRTL ? '16px' : 'auto',
                    left: isRTL ? 'auto' : '16px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    opacity: 0.6,
                    fontSize: '12px',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <span>
                    {language === 'ar' ? 'النسخة: ' : language === 'fr' ? 'Version: ' : 'Version: '}
                    {currentVersion}
                  </span>
                </div>
              )}
            </>
          )}
        </FooterWrapper>

        <MaxOpenTabsDialog />
        <CommandPalette />
        <FeedbackModal />
        <FeedbackSoundGate />
        <ImmediateActionFeedback />
        <FuelStationLiveToasts />
        <ScreenViewHost />
        {!isContentFullscreen && (
          <>
            <AutoUpdateManager checkOnStartup={true} checkInterval={5 * 60 * 1000} />
            <VoiceAssistant />
            <SmartGuideButton />
            <PageSettingsButton />
            <RecentItemsPanel />
          </>
        )}
        <UpdateNotification />
      </div>
    </div>
  )
}
